import { useEffect, useRef, useState, type ReactNode } from 'react'
import { BellRing, ChevronDown, ChevronRight } from 'lucide-react'
import { SignOutButton } from '../../core/index.js'

interface UserMenuProps {
  username: string
  firstName?: string | null
  lastName?: string | null
  organizationName?: string
  /** The person's job here ("Facility administrator"), shown in the panel with its icon. */
  role?: { label: string; icon: ReactNode; detail?: string | null }
  /** Opens the person's own notification settings; the panel lists it only when given. */
  onOpenNotifications?: () => void
  onSignOut: () => void
}

/**
 * The signed-in person, at the header's right: their name, which opens a
 * panel with who they are, their role here and their own notifications,
 * and the sign-out icon beside it. Keeps the header to one short row.
 */
export function UserMenu({ username, firstName, lastName, organizationName, role, onOpenNotifications, onSignOut }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : null
  const displayName = fullName ?? username
  const initials = fullName ? `${firstName!.charAt(0)}${lastName!.charAt(0)}`.toUpperCase() : username.charAt(0).toUpperCase()

  // A click anywhere else, or Escape, closes the panel.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    const onPointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-xs font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral ${isOpen ? 'bg-white/20' : 'bg-white/10 hover:bg-white/15'}`}
      >
        <Avatar initials={initials} />
        <span className="max-w-[160px] truncate">{displayName}</span>
        <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <SignOutButton onSignOut={onSignOut} />

      {isOpen && (
        <div role="region" aria-label="Your account" className="absolute right-0 top-full z-40 mt-2 w-72 overflow-hidden rounded-lg border border-marine/20 bg-white text-ink shadow-xl">
          <div className="flex items-center gap-2.5 border-b border-marine/10 px-4 py-3">
            <Avatar initials={initials} large />
            <div className="min-w-0">
              <div className="truncate text-sm font-bold">{displayName}</div>
              {fullName && <div className="truncate text-[11px] text-ink-mid">{username}</div>}
            </div>
          </div>
          {(role || organizationName) && (
            <div className="flex flex-col gap-0.5 border-b border-marine/10 px-4 py-3">
              <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Your role</span>
              {role && (
                <span className="flex items-center gap-1.5 text-xs font-bold">
                  <span className="text-marine">{role.icon}</span>
                  {role.label}
                </span>
              )}
              <span className="text-[11px] text-ink-mid">{[role?.detail, organizationName].filter(Boolean).join(' · ')}</span>
            </div>
          )}
          {onOpenNotifications && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                onOpenNotifications()
              }}
              className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-xs font-bold hover:bg-marine-lt/30 focus:outline-none focus-visible:bg-marine-lt/30"
            >
              <BellRing aria-hidden="true" className="h-4 w-4 text-marine" />
              Your notifications
              <ChevronRight aria-hidden="true" className="ml-auto h-4 w-4 text-ink-mid" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function Avatar({ initials, large = false }: { initials: string; large?: boolean }) {
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-full bg-coral font-bold text-white ${large ? 'h-9 w-9 text-[13px]' : 'h-6 w-6 text-[10px]'}`}>
      {initials}
    </span>
  )
}
